import { Button as MantineButton, Flex, Group, Modal, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import React from 'react';
import { Button } from 'src/design-system/Button';
import { AutoUpdateIndicator } from 'src/financial/financial-data/financial-item/forms/common/AutoUpdateIndicator';
import { saveLabelFor } from 'src/financial/financial-data/financial-item/rework/financialItemRework';
import { ReworkedFieldStyles } from 'src/financial/financial-data/financial-item/rework/ReworkedFieldStyles';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';

/** The header's own controls sit squarer than the design system's default pill. */
const SQUARER_RADIUS = { borderRadius: 12 };

type Props = {
  product: string;
  title: React.ReactNode;
  children?: React.ReactNode;
};

/** Reworked form shell: the item's title on the left, save and delete in the top right corner. */
export function FinancialItemFormHeader({ product, title, children }: Props) {
  return (
    <>
      <Group justify="space-between" align="center" mb="md" wrap="nowrap">
        {title}
        <HeaderActions product={product} />
      </Group>
      <ReworkedFieldStyles>{children}</ReworkedFieldStyles>
    </>
  );
}

function HeaderActions({ product }: { product: string }) {
  const { form, isEditing, isCreateMode, toggleEditing, onDelete } = useDynamicUI();
  const [warningModalOpened, { open: openWarningModal, close: closeWarningModal }] =
    useDisclosure(false);

  const formValues = form.getValues();
  const isAutomaticallyUpdated = Object.values(formValues).some(
    productEntity =>
      productEntity &&
      typeof productEntity === 'object' &&
      'is_updated_automatically' in productEntity &&
      productEntity.is_updated_automatically === 'true',
  );

  // Save is left available on a form the adviser has only just opened — greyed out, it reads as
  // broken rather than as "nothing to save yet". An untouched create form has nothing in it
  // though, and submitting it would file a blank item into the client's Financials, so it says
  // so instead of saving.
  const blockEmptyCreate = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isCreateMode || form.isDirty()) return;
    event.preventDefault();
    notifications.show({
      title: 'Nothing to save yet',
      message: 'Fill in at least one detail before saving this item.',
      color: 'red',
    });
  };

  return (
    <Group gap="sm" wrap="nowrap">
      {isAutomaticallyUpdated && <AutoUpdateIndicator />}
      <Modal opened={warningModalOpened} onClose={closeWarningModal} title="Are you sure?" centered>
        <Text c="gray.10">Once deleted you cannot recover this item.</Text>
        <Flex justify="flex-end" mt="xl">
          <MantineButton variant="pill" onClick={onDelete} type="button" mr="xs">
            Yes
          </MantineButton>
          <MantineButton variant="pill" onClick={closeWarningModal}>
            No
          </MantineButton>
        </Flex>
      </Modal>
      {isEditing && (
        <MantineButton
          data-testid="save-button"
          type="submit"
          loading={form.submitting}
          onClick={blockEmptyCreate}
        >
          {saveLabelFor(product)}
        </MantineButton>
      )}
      {/* An existing item opens read-only, so Edit is what makes the fields editable. Once they
          are, Save takes its place: an edit is left by saving it or by leaving the page. */}
      {!isCreateMode && !isEditing && (
        <Button
          variant="secondary"
          size="md"
          style={SQUARER_RADIUS}
          onClick={toggleEditing}
          data-testid="toggle-edit-button"
        >
          Edit
        </Button>
      )}
      {/* Deleting is an edit like any other, so it is offered once the adviser is editing
          rather than from the read-only view. */}
      {isEditing && !isCreateMode && onDelete && (
        <Button
          variant="secondary"
          size="md"
          style={SQUARER_RADIUS}
          onClick={openWarningModal}
          data-testid="delete-button"
        >
          Delete
        </Button>
      )}
    </Group>
  );
}
